import {useEffect, useState, useCallback} from "react";
import {useTranslation} from "react-i18next";
import {useAtom} from "jotai";
import {Box, IconButton, Stack, Typography} from "@mui/material";
import {
    MaterialReactTable,
    MRT_ColumnDef,
    useMaterialReactTable,
    MRT_VisibilityState,
    MRT_ColumnSizingState,
    MRT_Updater,
    MRT_ShowHideColumnsButton,
} from "material-react-table";
import {Alert} from "@mui/material";

import CheckIcon from "@mui/icons-material/Check";
import DeclineIcon from "@mui/icons-material/Close";
import {
    approveFieldChange,
    declineFieldChange,
    FieldChangeModelClient,
    getFieldChanges,
} from "../../services/fieldChangesService";
import {useSoils} from "../../hooks/soilsHooks";
import {useCrops} from "../../hooks/cropsHooks";
import {useIrrigations} from "../../hooks/irrigationsHooks";
import {usersLoadable} from "../../store/usersStore";
import {getUserFullName} from "../../utils/userUtils";
import {CropCycle} from "../../types/domain/cropCycle";
import {formatShortDataEmpty} from "../../utils/dateUtils";
import {User} from "../../types/domain/user";
import {useLoadableValueOrDefault} from "../../hooks/jotaiHooks";
import {getDefaultMRTOptions} from "../base/BaseTable";
import {authenticatedUserTableLocaleAtom} from "../../store/authenticatedUserStore";
import {
    adminFieldChangesTableColumnsVisibilityAtom,
    adminFieldChangesTableColumnsSizeAtom,
} from "../../store/adminFieldChangesTableStore";
import {useUserPreferences} from "../../hooks/userPreferencesHooks";
import {useCompanies} from "../../hooks/companiesHooks";

const DetailItem: React.FC<{
    label: string;
    value: {old: string | number | undefined; new: string | number | undefined};
}> = ({label, value}) => {
    const {t} = useTranslation();
    const PLACEHOLDER = "—";
    const oldValue = value.old === undefined || value.old === "" ? PLACEHOLDER : value.old;
    const newValue = value.new === undefined || value.new === "" ? PLACEHOLDER : value.new;

    if (oldValue === PLACEHOLDER && newValue === PLACEHOLDER) {
        return null;
    }
    if (oldValue === newValue) {
        return null;
    }

    return (
        <Typography>
            <span style={{fontWeight: "bold"}}>{`${label}: `}</span>
            {oldValue !== newValue ? (
                <>
                    <span style={{fontStyle: "italic"}}>{oldValue}</span>
                    {t("admin.field_changes_details_view.table.changed_to")}
                    <span>{newValue}</span>
                </>
            ) : (
                oldValue
            )}
        </Typography>
    );
};

const CropDetailItem: React.FC<{
    oldCropCycle?: CropCycle;
    newCropCycle?: CropCycle;
    cropsById: Record<string, {name: string}>;
}> = ({oldCropCycle, newCropCycle, cropsById}) => {
    const {t} = useTranslation();
    const {dateFormat} = useUserPreferences();
    if (!oldCropCycle && !newCropCycle) {
        return null;
    }
    const oldCrop = oldCropCycle?.cropId ? (cropsById[oldCropCycle.cropId]?.name ?? "") : "";
    const newCrop = newCropCycle?.cropId ? (cropsById[newCropCycle.cropId]?.name ?? "") : "";

    return (
        <>
            <DetailItem label={t("dialogs.field_create.crop_widget.crop")} value={{old: oldCrop, new: newCrop}} />
            <DetailItem
                label={t("dialogs.field_create.crop_widget.variety")}
                value={{old: oldCropCycle?.variety ?? "", new: newCropCycle?.variety ?? ""}}
            />
            <DetailItem
                label={t("dialogs.field_create.crop_widget.planted_date")}
                value={{
                    old: formatShortDataEmpty(oldCropCycle?.plantedDate, dateFormat),
                    new: formatShortDataEmpty(newCropCycle?.plantedDate, dateFormat),
                }}
            />
            <DetailItem
                label={t("dialogs.field_create.crop_widget.eff_root_depth")}
                value={{old: oldCropCycle?.effRootDepth ?? "", new: newCropCycle?.effRootDepth ?? ""}}
            />
            <DetailItem
                label={t("dialogs.field_create.crop_widget.act_harvested_date")}
                value={{
                    old: formatShortDataEmpty(oldCropCycle?.actHarvestDate, dateFormat),
                    new: formatShortDataEmpty(newCropCycle?.actHarvestDate, dateFormat),
                }}
            />
            <DetailItem
                label={t("dialogs.field_create.crop_widget.harvested_date")}
                value={{
                    old: formatShortDataEmpty(oldCropCycle?.estHarvestDate, dateFormat),
                    new: formatShortDataEmpty(newCropCycle?.estHarvestDate, dateFormat),
                }}
            />
            <DetailItem
                label={t("dialogs.field_create.form.drought_sensitivity")}
                value={{old: oldCropCycle?.droughtSensitivity ?? "", new: newCropCycle?.droughtSensitivity ?? ""}}
            />
        </>
    );
};

export const AdminFieldChangesView: React.FC = () => {
    const {t} = useTranslation();

    const {soilsById} = useSoils();
    const {irrigationsById} = useIrrigations();
    const {cropsById} = useCrops();
    const companies = useCompanies();
    const users: User[] = useLoadableValueOrDefault(usersLoadable, []);
    const [fieldChanges, setFieldChanges] = useState<FieldChangeModelClient[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [userMessage, setUserMessage] = useState<{severity: "info" | "error"; message: string} | null>(null);

    const [locale] = useAtom(authenticatedUserTableLocaleAtom);
    const defaultTableOptions = getDefaultMRTOptions<FieldChangeModelClient>(locale);

    const [adminFieldChangesTableColumnsVisibility, setAdminFieldChangesTableColumnsVisibility] = useAtom(
        adminFieldChangesTableColumnsVisibilityAtom,
    );
    const [adminFieldChangesTableColumnsSize, setAdminFieldChangesTableColumnsSize] = useAtom(
        adminFieldChangesTableColumnsSizeAtom,
    );

    const handleException = (error: unknown) => {
        if (error instanceof Error) {
            setUserMessage({severity: "error", message: error.message});
        } else {
            setUserMessage({severity: "error", message: "An unknown error occurred"});
        }
    };

    /* eslint-disable react-hooks/exhaustive-deps */
    const fetchFieldChanges = useCallback(async () => {
        setIsLoading(true);
        try {
            const data = await getFieldChanges();
            setFieldChanges(data);
        } catch (error: unknown) {
            handleException(error);
        } finally {
            setIsLoading(false);
        }
    }, []);
    useEffect(() => {
        void fetchFieldChanges();
    }, [fetchFieldChanges]);

    const handleApprove = async (fieldId: string) => {
        try {
            await approveFieldChange(fieldId);
            setUserMessage({
                severity: "info",
                message: t("admin.field_changes_details_view.field_change_approved", {id: fieldId}),
            });
            void fetchFieldChanges();
        } catch (error: unknown) {
            handleException(error);
        }
    };

    const handleDecline = async (fieldId: string) => {
        try {
            await declineFieldChange(fieldId);
            void fetchFieldChanges();
        } catch (error: unknown) {
            handleException(error);
        }
    };

    const handleCloseAlert = () => {
        setUserMessage(null);
    };

    const columns: MRT_ColumnDef<FieldChangeModelClient>[] = [
        {
            id: "fieldId",
            header: t("admin.field_changes_details_view.table.field_id"),
            accessorKey: "id",
            filterFn: "contains",
            size: 150,
        },
        {
            id: "username",
            header: t("admin.field_changes_details_view.table.username"),
            accessorFn: (row) => {
                const userId = row.userId;
                const user = users.find((user) => user.id === userId);
                return user ? getUserFullName(user) : "";
            },
            filterFn: "contains",
            size: 250,
        },
        {
            id: "company",
            header: t("admin.field_changes_details_view.table.company"),
            accessorFn: (row) =>
                (companies.isSuccess ? companies.data : []).find((company) => company.id === row.companyId)?.properties
                    .name,
            filterFn: "contains",
        },
        {
            id: "name",
            header: t("admin.field_changes_details_view.table.field_name"),
            accessorKey: "fieldName",
            filterFn: "contains",
        },
        {
            id: "changeDate",
            header: t("admin.field_changes_details_view.table.change_date"),
            accessorKey: "changeDate",
            filterFn: "contains",
            Cell: ({cell}) => new Date(cell.getValue<string>()).toLocaleString(),
        },
    ];

    const onColumnVisibilityChange = (v: MRT_VisibilityState | MRT_Updater<MRT_VisibilityState>) => {
        setAdminFieldChangesTableColumnsVisibility(v);
    };

    const onColumnSizingChange = (v: MRT_ColumnSizingState | MRT_Updater<MRT_ColumnSizingState>) => {
        setAdminFieldChangesTableColumnsSize(v as Record<string, number>);
    };

    const table = useMaterialReactTable({
        ...defaultTableOptions,
        muiTableContainerProps: {
            sx: {
                maxHeight: "calc(100vh - 12rem)",
                minHeight: "calc(100vh - 12rem)",
            },
        },
        initialState: {
            ...defaultTableOptions.initialState,
            columnPinning: {
                right: ["mrt-row-actions"],
            },
            pagination: {
                pageIndex: 0,
                pageSize: 20,
            },
        },
        columns,
        data: fieldChanges,
        state: {
            isLoading,
            columnVisibility: adminFieldChangesTableColumnsVisibility,
            ...(adminFieldChangesTableColumnsSize && {columnSizing: adminFieldChangesTableColumnsSize}),
        },
        enableExpanding: true,
        muiTableBodyCellProps: {
            sx: {
                whiteSpace: "nowrap",
                paddingTop: "0.5rem",
                paddingBottom: "0.5rem",
            },
        },
        enableColumnResizing: true,
        onColumnVisibilityChange: onColumnVisibilityChange,
        onColumnSizingChange: onColumnSizingChange,
        displayColumnDefOptions: {
            "mrt-row-expand": {
                size: 40,
            },
            "mrt-row-actions": {
                enableResizing: false,
                size: 140,
                header: t("admin.field_changes_details_view.table.approve_change"),
            },
        },
        muiTableHeadRowProps: () => ({sx: {backgroundColor: "#F3F5FA"}}),
        renderRowActions: ({row}) => (
            <Stack direction="row" spacing={0.5}>
                <IconButton color="primary" onClick={() => void handleApprove(row.original.id)}>
                    <CheckIcon />
                </IconButton>
                <IconButton color="error" onClick={() => void handleDecline(row.original.id)}>
                    <DeclineIcon />
                </IconButton>
            </Stack>
        ),

        renderDetailPanel: ({row}) => (
            <Box sx={{p: 2}}>
                <Typography variant="h6" gutterBottom component="div">
                    {t("admin.field_changes_details_view.table.field_details")}
                </Typography>
                <Stack spacing={1}>
                    {(() => {
                        const field = row.original || {};
                        const oldCropCycle: CropCycle | undefined = field.cropCycles?.old?.[0];
                        const newCropCycle: CropCycle | undefined = field.cropCycles?.new?.[0];

                        const oldIrrigation =
                            (irrigationsById as Record<string, {name: string}>)[field.irrigationTypeId?.old ?? ""]
                                ?.name ?? "";
                        const newIrrigation =
                            (irrigationsById as Record<string, {name: string}>)[field.irrigationTypeId?.new ?? ""]
                                ?.name ?? "";

                        const oldSoil =
                            (soilsById as Record<string, {name: string}>)[field.soilId?.old ?? ""]?.name ?? "";
                        const newSoil =
                            (soilsById as Record<string, {name: string}>)[field.soilId?.new ?? ""]?.name ?? "";

                        return (
                            <>
                                <DetailItem
                                    label={t("dialogs.field_create.form.field_name")}
                                    value={{old: field.name?.old, new: field.name?.new}}
                                />
                                <DetailItem label={t("Soil")} value={{old: oldSoil, new: newSoil}} />
                                <DetailItem
                                    label={t("dialogs.field_create.form.irrigation_type")}
                                    value={{old: oldIrrigation, new: newIrrigation}}
                                />
                                <DetailItem
                                    label={t("dialogs.field_create.form.priority")}
                                    value={{old: field.priority?.old, new: field.priority?.new}}
                                />
                                <DetailItem
                                    label={t("dialogs.field_create.form.irrigation_interval")}
                                    value={{old: field.irrigationInterval?.old, new: field.irrigationInterval?.new}}
                                />
                                <DetailItem
                                    label={t("dialogs.field_create.form.moisture_level")}
                                    value={{old: field.desiredMoistureLevel?.old, new: field.desiredMoistureLevel?.new}}
                                />
                                <DetailItem
                                    label={t("dialogs.field_create.form.irrigation_criteria")}
                                    value={{old: field.irrigationCriteria?.old, new: field.irrigationCriteria?.new}}
                                />
                                <DetailItem
                                    label={t("dialogs.field_create.form.irrigation_default")}
                                    value={{old: field.irrigationDefault?.old, new: field.irrigationDefault?.new}}
                                />
                                <DetailItem
                                    label={t("dialogs.field_create.form.sat_soil_moisture_bias")}
                                    value={{old: field.satSoilMoistBias?.old, new: field.satSoilMoistBias?.new}}
                                />
                                <DetailItem
                                    label={t("dialogs.field_create.form.ksat_bias")}
                                    value={{old: field.KSATBias?.old, new: field.KSATBias?.new}}
                                />
                                <CropDetailItem
                                    oldCropCycle={oldCropCycle}
                                    newCropCycle={newCropCycle}
                                    cropsById={cropsById as Record<string, {name: string}>}
                                />
                                <DetailItem
                                    label={t("dialogs.field_create.form.order")}
                                    value={{old: field.orderId?.old, new: field.orderId?.new}}
                                />
                                <DetailItem
                                    label={t("dialogs.field_create.form.farm")}
                                    value={{old: field.farmId?.old, new: field.farmId?.new}}
                                />
                                <DetailItem
                                    label={t("dialogs.field_create.form.parcel_id")}
                                    value={{old: field.parcelId?.old, new: field.parcelId?.new}}
                                />
                                <DetailItem
                                    label={t("dialogs.field_create.form.on_farm_efficiency_min")}
                                    value={{old: field.onFarmEfficiencyMin?.old, new: field.onFarmEfficiencyMin?.new}}
                                />
                                <DetailItem
                                    label={t("dialogs.field_create.form.min_applied_water")}
                                    value={{old: field.minAppliedWater?.old, new: field.minAppliedWater?.new}}
                                />
                                <DetailItem
                                    label={t("dialogs.field_create.form.max_applied_water")}
                                    value={{old: field.maxAppliedWater?.old, new: field.maxAppliedWater?.new}}
                                />
                            </>
                        );
                    })()}
                </Stack>
            </Box>
        ),
        muiExpandButtonProps: {
            sx: {
                fontSize: "0.75rem",
            },
        },
    });

    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(() => {
        if (fieldChanges && fieldChanges.length > 0) {
            setFieldChanges([...fieldChanges]);
        }
    }, [companies?.data, users]);

    return (
        <Stack sx={{px: "1.5rem", pb: "0.5rem"}}>
            <Stack direction="row" height="4rem" alignItems="center" justifyContent="space-between">
                <Typography fontSize={20} fontWeight={500} textTransform="uppercase">
                    {t("admin_page.menu.fieldChanges")}
                </Typography>
                <MRT_ShowHideColumnsButton table={table} />
            </Stack>
            {userMessage && (
                <Alert severity={userMessage.severity} sx={{marginBottom: "1rem"}} onClose={handleCloseAlert}>
                    {userMessage.message}
                </Alert>
            )}
            <MaterialReactTable table={table} />
        </Stack>
    );
};
